import RequisitesFormDTO from "@/models/form/RequisitesFormDTO";
import RequisiteTemplateDTO from "@/models/RequisiteTemplateDTO";
import {RequisiteTemplateFields} from "@/models/request/RequisiteTemplateRequestDTO";
import {PaymentOption} from "@/models/enum/PaymentOption";
import {crossBorderSwift} from "@/service/crossBorderSwift";

export interface TemplateFieldFlags {
  allDealsCountLimits: boolean;
  totalLimits: boolean;
  multiples: boolean;
  reuse: boolean;
  reusablePaymentsCountLimit: boolean;
}

export interface TemplateAvailability {
  currencies: string[];
  paymentOptions: string[];
  methods: string[];
  paymentSystems: string[];
  crossBorderCurrencies: string[];
  groupIds: number[];
  deviceIds: string[];
}

export type StaleTemplateField = 'currency' | 'paymentOption' | 'method' | 'paymentSystem' | 'crossBorderCurrency' | 'groupId' | 'deviceId';

export interface TemplateFormPatch {
  head: Partial<RequisitesFormDTO>;
  body: Partial<RequisitesFormDTO>;
  swift: string | null;
}

const CROSS_BORDER_CURRENCY_OPTIONS: string[] = [
  PaymentOption.CROSS_BORDER,
  PaymentOption.MOBILE_TOP_UP,
  PaymentOption.SBP_QR,
  PaymentOption.CARD_ACQUIRING_REDIRECT,
];

const STALE_FIELDS: StaleTemplateField[] = ['currency', 'paymentOption', 'method', 'paymentSystem', 'crossBorderCurrency', 'groupId', 'deviceId'];

const positiveInt = (value: unknown): number | null => {
  const raw = `${value ?? ''}`.replace(/\s/g, '');
  if (!/^\d+$/.test(raw)) {
    return null;
  }
  const parsed = Number.parseInt(raw, 10);

  return parsed > 0 ? parsed : null;
};

export const buildTemplateFields = (form: RequisitesFormDTO, flags: TemplateFieldFlags, paymentSystemOptions: string[]): RequisiteTemplateFields => {
  const fields: Record<string, string | number | boolean> = {};
  const text = (key: string, value: unknown) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (normalized) {
      fields[key] = normalized;
    }
  };
  const int = (key: string, value: unknown) => {
    const parsed = positiveInt(value);
    if (parsed !== null) {
      fields[key] = parsed;
    }
  };
  const flag = (key: string, value: unknown) => {
    if (value === true) {
      fields[key] = true;
    }
  };

  text('currency', form.currency);
  text('paymentOption', form.paymentOption);
  text('method', form.method);
  if (form.paymentSystem && paymentSystemOptions.includes(form.paymentSystem)) {
    text('paymentSystem', form.paymentSystem);
  }
  if (CROSS_BORDER_CURRENCY_OPTIONS.includes(form.paymentOption ?? '')) {
    text('crossBorderCurrency', form.crossBorderCurrency);
  }
  if (form.paymentOption === PaymentOption.CROSS_BORDER) {
    text('swift', crossBorderSwift(form.raw));
  }
  text('deviceId', form.deviceId);
  int('groupId', form.groupId);

  int('minPayInDealSum', form.minPayInDealSum);
  int('maxPayInDealSum', form.maxPayInDealSum);
  if (flags.multiples) {
    int('multiplesOfValue', form.multiplesOfValue);
  }

  int('dayTradesSumLimit', form.dayTradesSumLimit);
  int('monthTradesSumLimit', form.monthTradesSumLimit);
  int('dayTradesCountLimit', form.dayTradesCountLimit);
  int('monthTradesCountLimit', form.monthTradesCountLimit);
  if (flags.allDealsCountLimits) {
    int('dayTradesAllCountLimit', form.dayTradesAllCountLimit);
    int('monthTradesAllCountLimit', form.monthTradesAllCountLimit);
  }
  if (flags.totalLimits) {
    int('totalTradesSumLimit', form.totalTradesSumLimit);
    int('totalTradesCountLimit', form.totalTradesCountLimit);
    flag('manualResetEnabled', form.manualResetEnabled);
  }

  if (flags.reuse) {
    int('maxReuseCount', form.maxReuseCount);
    if (fields.maxReuseCount !== undefined) {
      int('reuseDaysCount', form.reuseDaysCount);
      if (flags.reusablePaymentsCountLimit) {
        int('reusablePaymentsCountLimit', form.reusablePaymentsCountLimit);
      }
    }
  }

  flag('isActive', form.isActive);
  flag('onlyInternalTransfer', form.onlyInternalTransfer);
  flag('isFarmsSettingsEnabled', form.isFarmsSettingsEnabled);
  if (form.isFarmsSettingsEnabled === true) {
    int('concurrentTradesCount', form.concurrentTradesCount);
    int('tradesTimeoutMinutes', form.tradesTimeoutMinutes);
  }

  if (form.paymentOption === PaymentOption.VIET_QR) {
    flag('vietQrIsStatic', form.vietQrIsStatic);
  }
  if (form.paymentOption === PaymentOption.IND_QR) {
    flag('qrisIsStatic', form.qrisIsStatic);
  }
  if (form.paymentOption === PaymentOption.BR_QR) {
    flag('pixIsStatic', form.pixIsStatic);
  }

  return fields as RequisiteTemplateFields;
};

export const templateToFormPatch = (template: RequisiteTemplateDTO, flags: TemplateFieldFlags): TemplateFormPatch => {
  const head: Record<string, unknown> = {};
  const body: Record<string, unknown> = {};
  const set = (target: Record<string, unknown>, key: keyof RequisiteTemplateDTO, transform: (value: any) => unknown = (value) => value) => {
    const value = template[key];
    if (value !== null && value !== undefined) {
      target[key] = transform(value);
    }
  };
  const asSum = (value: number) => String(value);

  set(head, 'currency');
  set(head, 'paymentOption');
  set(head, 'crossBorderCurrency');

  set(body, 'method');
  set(body, 'paymentSystem');
  set(body, 'deviceId');
  set(body, 'groupId');
  set(body, 'minPayInDealSum', asSum);
  set(body, 'maxPayInDealSum', asSum);
  if (flags.multiples) {
    set(body, 'multiplesOfValue', asSum);
  }
  set(body, 'dayTradesSumLimit', asSum);
  set(body, 'monthTradesSumLimit', asSum);
  set(body, 'dayTradesCountLimit');
  set(body, 'monthTradesCountLimit');
  if (flags.allDealsCountLimits) {
    set(body, 'dayTradesAllCountLimit');
    set(body, 'monthTradesAllCountLimit');
  }
  if (flags.totalLimits) {
    set(body, 'totalTradesSumLimit', asSum);
    set(body, 'totalTradesCountLimit');
    set(body, 'manualResetEnabled');
  }
  if (flags.reuse) {
    set(body, 'maxReuseCount');
    set(body, 'reuseDaysCount');
    if (flags.reusablePaymentsCountLimit) {
      set(body, 'reusablePaymentsCountLimit');
    }
  }
  set(body, 'isActive');
  set(body, 'onlyInternalTransfer');
  set(body, 'isFarmsSettingsEnabled');
  if (template.isFarmsSettingsEnabled === true) {
    set(body, 'concurrentTradesCount');
    set(body, 'tradesTimeoutMinutes');
  }
  set(body, 'vietQrIsStatic');
  set(body, 'qrisIsStatic');
  set(body, 'pixIsStatic');

  return {
    head: head as Partial<RequisitesFormDTO>,
    body: body as Partial<RequisitesFormDTO>,
    swift: template.swift ?? null,
  };
};

export const findStaleTemplateFields = (
  template: RequisiteTemplateDTO,
  form: RequisitesFormDTO,
  availability: TemplateAvailability,
): StaleTemplateField[] => {
  const isAvailable: Record<StaleTemplateField, (value: any) => boolean> = {
    currency: (value) => availability.currencies.includes(value),
    paymentOption: (value) => availability.paymentOptions.includes(value),
    method: (value) => availability.methods.includes(value),
    paymentSystem: (value) => availability.paymentSystems.includes(value),
    crossBorderCurrency: (value) => availability.crossBorderCurrencies.includes(value),
    groupId: (value) => availability.groupIds.includes(value),
    deviceId: (value) => availability.deviceIds.includes(value),
  };

  return STALE_FIELDS.filter((field) => {
    const value = template[field];
    if (value === null || value === undefined) {
      return false;
    }
    if (field === 'paymentSystem' && availability.paymentSystems.length === 0) {
      return false;
    }
    if (field === 'crossBorderCurrency' && !CROSS_BORDER_CURRENCY_OPTIONS.includes(form.paymentOption ?? '')) {
      return false;
    }

    return form[field] === value && !isAvailable[field](value);
  });
};
